<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { object, number } from 'yup';
import { holidayMultipliersApi, type HolidayMultiplier } from '@/api/holiday-multipliers';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';

const { t } = useI18n();
const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const queryClient = useQueryClient();
const MAX_MULTIPLIER = 500;

const { data: holidayMultipliers, isLoading } = useQuery({
  queryKey: ['holidayMultipliers'],
  queryFn: holidayMultipliersApi.index,
  initialData: [],
});

const headerClass = 'font-weight-bold';
const headers = [
  { title: 'Tipo de Feriado', key: 'humanHolidayType', headerProps: { class: headerClass } },
  { title: 'Recargo Precio', key: 'priceMultiplier', headerProps: { class: headerClass } },
  { title: 'Recargo Pago a Profesional', key: 'costMultiplier', headerProps: { class: headerClass } },
  { title: 'Acciones', key: 'actions', sortable: false, headerProps: { class: headerClass } },
];

const editingItem = ref<HolidayMultiplier | null>(null);

const multiplierField = number()
  .required(t('baseServicePriceForm.fieldRequired'))
  .typeError(t('baseServicePriceForm.fieldRequired'))
  .integer(t('baseServicePriceForm.fieldPositive'))
  .min(0, t('baseServicePriceForm.fieldPositive'))
  .max(MAX_MULTIPLIER, `Máximo ${MAX_MULTIPLIER}%`);

const validationSchema = object({
  priceMultiplier: multiplierField,
  costMultiplier: multiplierField,
});

const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    priceMultiplier: 0,
    costMultiplier: 0,
  },
});

const [priceMultiplier, priceMultiplierAttrs] = defineField('priceMultiplier');
const [costMultiplier, costMultiplierAttrs] = defineField('costMultiplier');

function openEditDialog(item: HolidayMultiplier) {
  editingItem.value = item;
  resetForm({
    values: {
      priceMultiplier: item.priceMultiplier,
      costMultiplier: item.costMultiplier,
    },
  });
}

function closeEditDialog() {
  editingItem.value = null;
}

const {
  mutate: updateMultiplier,
  isPending: isUpdatePending,
} = useMutation({
  mutationFn: (data: { id: number; priceMultiplier: number; costMultiplier: number }) =>
    holidayMultipliersApi.update(data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['holidayMultipliers'] });
    closeEditDialog();
  },
});

const onSubmit = handleSubmit((values) => {
  if (!editingItem.value) return;

  updateMultiplier({
    id: editingItem.value.id,
    priceMultiplier: values.priceMultiplier,
    costMultiplier: values.costMultiplier,
  });
});
</script>

<template>
  <v-card v-if="hasPermission('service_prices', 'index')">
    <v-card-title>
      <span class="font-weight-bold">Recargos por Feriados</span>
    </v-card-title>
    <v-card-subtitle class="text-wrap">
      Configura el porcentaje de recargo que se aplica al precio y pago de turnos en días feriados.
    </v-card-subtitle>
    <v-card-text>
      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :items="holidayMultipliers"
        :loading="isLoading"
        loading-text="Cargando recargos..."
        no-data-text="No hay recargos configurados"
      >
        <template #[`item.priceMultiplier`]="{ item }">
          {{ item.priceMultiplier }}%
        </template>
        <template #[`item.costMultiplier`]="{ item }">
          {{ item.costMultiplier }}%
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="hasPermission('service_prices', 'update')"
            icon="mdi-pencil"
            size="small"
            @click="openEditDialog(item)"
          />
        </template>
      </v-data-table>

      <v-dialog
        :model-value="!!editingItem"
        max-width="500"
        @update:model-value="(val: boolean) => { if (!val) closeEditDialog(); }"
      >
        <v-card v-if="editingItem">
          <v-card-title>
            <span class="text-h5">Editar {{ editingItem.humanHolidayType }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="priceMultiplier"
                    v-bind="priceMultiplierAttrs"
                    label="Recargo Precio (%)"
                    type="number"
                    variant="outlined"
                    suffix="%"
                    :min="0"
                    :max="MAX_MULTIPLIER"
                    :error-messages="errors.priceMultiplier"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="costMultiplier"
                    v-bind="costMultiplierAttrs"
                    label="Recargo Pago a Profesional (%)"
                    type="number"
                    variant="outlined"
                    suffix="%"
                    :min="0"
                    :max="MAX_MULTIPLIER"
                    :error-messages="errors.costMultiplier"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn
              variant="text"
              @click="closeEditDialog"
            >
              Cancelar
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              variant="text"
              :loading="isUpdatePending"
              @click="onSubmit"
            >
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>
